import type { 
  ApiResponse, 
  User, 
  Settings, 
  Task, 
  LLMJob, 
  LLMJobProgress,
  NotionDatabase,
  TaskStatus,
  TaskPriority,
  LLMJobType,
  LLMProvider,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Generic fetch wrapper
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  
  return data;
}

// Auth API
export const authApi = {
  getLoginUrl: () => `${API_URL}/auth/notion/login`,
  
  getCurrentUser: async () => {
    return fetchApi<{ user: User; settings: Settings | null }>('/auth/me');
  },
  
  logout: async () => {
    return fetchApi<void>('/auth/logout', { method: 'POST' });
  },
};

// Tasks API
export const tasksApi = {
  list: async () => {
    return fetchApi<{ tasks: Task[]; hasDatabase: boolean; message?: string }>('/tasks');
  },
  
  create: async (task: {
    title: string;
    status?: TaskStatus;
    priority?: TaskPriority;
    dueDate?: string | null;
    tags?: string[];
  }) => {
    return fetchApi<Task>('/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    });
  },
  
  get: async (id: string) => {
    return fetchApi<Task>(`/tasks/${id}`);
  },
  
  update: async (id: string, updates: Partial<Task>) => {
    return fetchApi<Task>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },
  
  delete: async (id: string) => {
    return fetchApi<{ id: string; archived: boolean }>(`/tasks/${id}`, {
      method: 'DELETE',
    });
  },
  
  complete: async (id: string) => {
    return fetchApi<Task>(`/tasks/${id}/complete`, {
      method: 'POST',
    });
  },
};

// LLM API
export const llmApi = {
  execute: async (params: {
    taskId: string;
    jobType: LLMJobType;
    additionalPrompt?: string;
  }) => {
    return fetchApi<{ jobId: string; status: string }>('/llm/execute', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
  
  getJobStatus: async (jobId: string) => {
    return fetchApi<LLMJobProgress>(`/llm/jobs/${jobId}/status`);
  },
  
  listJobs: async (limit?: number) => {
    const params = limit ? `?limit=${limit}` : '';
    return fetchApi<LLMJob[]>(`/llm/jobs${params}`);
  },
  
  cancelJob: async (jobId: string) => {
    return fetchApi<{ cancelled: boolean }>(`/llm/jobs/${jobId}/cancel`, {
      method: 'POST',
    });
  },
  
  retryJob: async (jobId: string) => {
    return fetchApi<{ jobId: string; status: string; retriedFromJobId: string }>(
      `/llm/jobs/${jobId}/retry`,
      { method: 'POST' }
    );
  },
};

// Settings API
export const settingsApi = {
  get: async () => {
    return fetchApi<Settings>('/settings');
  },
  
  update: async (updates: Partial<Settings>) => {
    return fetchApi<Settings>('/settings', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },
  
  setLlmKey: async (params: { llmProvider: LLMProvider; llmApiKey: string }) => {
    return fetchApi<{ llmProvider: LLMProvider; hasLlmApiKey: boolean }>(
      '/settings/llm-key',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  },
  
  removeLlmKey: async () => {
    return fetchApi<{ hasLlmApiKey: boolean }>('/settings/llm-key', {
      method: 'DELETE',
    });
  },
  
  listNotionDatabases: async () => {
    return fetchApi<NotionDatabase[]>('/settings/notion-databases');
  },
  
  setNotionDatabase: async (databaseId: string) => {
    return fetchApi<{ databaseId: string; databaseTitle: string }>(
      '/settings/notion-database',
      {
        method: 'POST',
        body: JSON.stringify({ databaseId }),
      }
    );
  },
  
  createOceanDatabase: async () => {
    return fetchApi<{ databaseId: string; databaseTitle: string; message: string }>(
      '/settings/notion-database/create',
      { method: 'POST' }
    );
  },
};

